import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  TextField,
  Button,
  DataTable,
  Text,
  Box,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import reportsApi from '@/services/reports';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

function formatCurrency(dollars: number): string {
  return `$${Number(dollars).toFixed(2)}`;
}

interface StatCardProps {
  label: string;
  value: string;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" variant="bodyMd" tone="subdued">
          {label}
        </Text>
        <Text as="p" variant="headingLg">
          {value}
        </Text>
      </BlockStack>
    </Card>
  );
}

export default function AgentCommission() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['report', 'agent-commission', startDate, endDate],
    queryFn: () => reportsApi.getAgentCommission({ startDate, endDate }),
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await reportsApi.downloadCsv('/reports/agent-commission', { startDate, endDate });
    } finally {
      setExporting(false);
    }
  };

  // API returns data as a flat array of agent objects
  const agents = Array.isArray(data?.data) ? data.data : [];

  const rows =
    agents.map((item) => [
      item.agentName,
      formatCurrency(item.totalCommission),
      formatCurrency(item.pendingCommission),
      formatCurrency(item.paidCommission),
      item.rentalCount,
      item.clinicCount,
    ]);

  return (
    <Page
      title="Agent Commission"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="400" blockAlign="end">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={setStartDate}
              autoComplete="off"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={setEndDate}
              autoComplete="off"
            />
            <Box paddingBlockStart="600">
              <InlineStack gap="200">
                <Button onClick={() => refetch()}>Refresh</Button>
                <Button onClick={handleExport} loading={exporting}>
                  Export CSV
                </Button>
              </InlineStack>
            </Box>
          </InlineStack>

          {isLoading ? (
            <SkeletonBodyText lines={8} />
          ) : isError ? (
            <EmptyState heading="Failed to load report" image="">
              <p>There was an error loading the Agent Commission report. Try refreshing.</p>
            </EmptyState>
          ) : !data ? (
            <EmptyState heading="No data" image="">
              <p>No commission data found for the selected date range.</p>
            </EmptyState>
          ) : (
            <BlockStack gap="400">
              <InlineGrid columns={3} gap="400">
                <StatCard label="Total Commission" value={formatCurrency(agents.reduce((s, a) => s + a.totalCommission, 0))} />
                <StatCard label="Pending" value={formatCurrency(agents.reduce((s, a) => s + a.pendingCommission, 0))} />
                <StatCard label="Paid" value={formatCurrency(agents.reduce((s, a) => s + a.paidCommission, 0))} />
              </InlineGrid>

              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric']}
                headings={['Agent', 'Total', 'Pending', 'Paid', 'Rentals', 'Clinics']}
                rows={rows}
                footerContent={rows.length === 0 ? 'No records found' : undefined}
              />
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}
